import PropTypes from 'prop-types';
import {
  filterArrayUnique,
  findObjectByProperty,
  formatIntFromTime,
  getJoinedAreaIds,
  getNearestInArrayOfObjects,
  isEmpty,
  isEmptyObject,
  isNil,
  minutesToTimeString,
  sortByProperty,
  timeStringToTime,
  unique,
  updateObjectByProperty,
} from '../../utils/helpers';
import { ALL_SESSIONS_LABEL, getSessionLabel } from './obeeSessions';

export const obeeTablePropTypes = PropTypes.shape({
  id: PropTypes.number,
  areaId: PropTypes.number,
  tableNumber: PropTypes.string,
  tableName: PropTypes.string,
  min: PropTypes.number,
  max: PropTypes.number,
  priority: PropTypes.number,
});

export const obeeAvailabilitySuggestionPropTypes = PropTypes.shape({
  restId: PropTypes.string,
  time: PropTypes.number,
  table: obeeTablePropTypes,
  tables: PropTypes.arrayOf(obeeTablePropTypes),
});

export const obeeAvailabilitySuggestionsPropTypes = PropTypes.shape({
  data: PropTypes.arrayOf(obeeAvailabilitySuggestionPropTypes),
  error: PropTypes.string,
  pending: PropTypes.bool,
  success: PropTypes.bool,
});

/**
 * Checks if the created booking is within the area selected
 * @param {*} booking
 * @param {*} obeeBooking
 * @param {*} session
 */
const isBookingWithinArea = (booking, obeeBooking, session) => {
  return booking.areaId === -1 || obeeBooking.areaId === session.areaId;
};

/**
 * Checks if the created booking is within the time range of the selected session
 * @param {*} obeeBooking
 * @param {*} session
 */
const isBookingWithinTime = (obeeBooking, session) => {
  return obeeBooking.time >= session.startTime && obeeBooking.time <= session.endTime;
};

/**
 * Checks if the created booking has the same date as the selected date
 * @param {*} obeeBooking
 * @param {*} booking
 * @returns
 */
const isBookingForDate = (obeeBooking, booking) => {
  return obeeBooking.date === booking.date;
};

/**
 * Checks if the created booking has the same size as the selected guests size
 * @param {*} obeeBooking
 * @param {*} booking
 * @returns
 */
const isBookingForSize = (obeeBooking, booking) => {
  return obeeBooking.size === booking.guests;
};

/**
 * Gets the created booking if the criteria are met
 * @param {*} booking
 * @param {*} obeeBooking
 * @param {string} restId
 * @param {*} session
 * @returns
 */
const getBookedSuggestion = (booking, obeeBooking, restId, session) => {
  if (isEmptyObject(obeeBooking)) {
    return null;
  }

  if (!isBookingWithinArea(booking, obeeBooking, session)) {
    return null;
  }

  if (!isBookingWithinTime(obeeBooking, session)) {
    return null;
  }

  if (!isBookingForDate(obeeBooking, booking)) {
    return null;
  }

  if (!isBookingForSize(obeeBooking, booking)) {
    return null;
  }

  return {
    restId,
    table: {
      areaId: obeeBooking.areaId,
      tableNumber: obeeBooking.tableNumber,
    },
    tables: [
      {
        areaId: obeeBooking.areaId,
        tableNumber: obeeBooking.tableNumber,
      },
    ],
    time: obeeBooking.time,
    waitlist: obeeBooking.status === 'waitlist',
    allAreas: booking.areaId === -1,
  };
};

/**
 * Get all available suggestions depending on `session`, if `waitlist` is `on` and if `allAreas`
 * @param {*} availabilitySuggestions
 * @param {*} session
 * @param {*} restaurant
 * @param {*} booking
 * @param {*} obeeBooking
 * @returns
 */
export const getAvailabilitySuggestions = (
  availabilitySuggestions,
  booking,
  obeeBooking,
  restaurant,
  session,
) => {
  if (!session) {
    return [];
  }

  const { startTime, endTime } = session;

  // construct a suggestion to show so a user can reselect their current booking time in the edit flow
  const bookedSuggestion = getBookedSuggestion(booking, obeeBooking, restaurant.objectId, session);

  const sessionSuggestions = availabilitySuggestions.filter(
    (availabilitySuggestion) =>
      availabilitySuggestion.time >= startTime && availabilitySuggestion.time < endTime,
  );

  return [
    ...(isNil(bookedSuggestion) ? [] : [bookedSuggestion]), // place first so it has priority for `unique()`
    ...sessionSuggestions.map((sessionSuggestion) => ({
      ...sessionSuggestion,
      allAreas: booking.areaId === -1,
      session,
    })),
  ];
};

/**
 * Gets all the suggestions for a sessionId
 * @param {*} booking
 * @param {*} sessions
 * @param {*} availabilitySuggestions
 * @param {*} obeeBooking
 * @param {*} restaurant
 * @param allSessions
 * @returns
 */
export const getAvailabilitySuggestionsForSession = (
  booking,
  sessions,
  availabilitySuggestions,
  obeeBooking,
  restaurant,
  allSessions, // Used to know if this is overriding default sessions
  filteredLabel = null,
) => {
  if (isEmpty(availabilitySuggestions)) {
    return [];
  }

  const areasThatOverrideDefaultHours = filterArrayUnique(
    allSessions.map((session) => session.areaId),
  ).filter((area) => area.areaId !== 0);

  let availabilities = sessions
    .filter((session) => {
      if (!filteredLabel || filteredLabel === ALL_SESSIONS_LABEL) {
        // "All" sessions selected
        return true;
      }

      // Specific session selected
      return `${getSessionLabel(session.label)}`.toLowerCase() === `${filteredLabel}`.toLowerCase();
    })
    .flatMap((session) => {
      const availabilitiesForSession = getAvailabilitySuggestions(
        availabilitySuggestions,
        booking,
        obeeBooking,
        restaurant,
        session,
      );
      // If the session has sitting times, we need to filter availabilities to only those times
      if (!isEmpty(session.sittingTimes)) {
        const allowedTimes = session.sittingTimes
          .flatMap((st) => st.times)
          .map((time) => formatIntFromTime(timeStringToTime(time, 'h:mma')));

        return availabilitiesForSession.filter((avail) => allowedTimes.includes(avail.time));
      }

      return availabilitiesForSession;
    })
    .map((availabilitySuggestion) => {
      // Filter out some availabilities and modify the data where needed
      if (isEmpty(availabilitySuggestion)) {
        return null;
      }

      // Return the availability suggestion with only the tables it supports
      // Filter out tables that aren't in this session (e.g. overridden by another session)
      const tablesForSuggestion =
        availabilitySuggestion?.tables ??
        (!isEmpty(availabilitySuggestion?.table) ? [availabilitySuggestion?.table] : []);

      const tablesForAvailability = unique(
        tablesForSuggestion
          .filter(
            (table) =>
              isEmpty(availabilitySuggestion?.session) ||
              (availabilitySuggestion.session.areaId <= 0 &&
                !areasThatOverrideDefaultHours.includes(table.areaId)) ||
              getJoinedAreaIds(
                availabilitySuggestion.session.areaId,
                restaurant.joinedAreas,
              ).includes(table.areaId),
          )
          .map((table) => ({ ...table, key: `${table?.areaId}-${table?.id}` })),
        'key',
      );

      // Add the sitting time label if this is a sitting time session
      const hasSittingTimes = !isEmpty(availabilitySuggestion?.session?.sittingTimes);

      const timeAsObeeString = minutesToTimeString(availabilitySuggestion.time);
      const getSittingTimeLabel = () =>
        availabilitySuggestion.session.sittingTimes.find((sittingTimeGroup) =>
          sittingTimeGroup.times.includes(timeAsObeeString),
        )?.label ?? availabilitySuggestion?.session?.label;

      // This is where we'll add the sitting time labels
      const tablesWithAreaIncluded = tablesForAvailability.map((table) => {
        const areaName =
          findObjectByProperty(restaurant.areas, table?.areaId, 'id')?.name ??
          restaurant.joinedAreas?.find((j) => j.areaIds.includes(String(table?.areaId)))
            ?.joinedAreaName;
        const sittingTimeLabel = hasSittingTimes ? getSittingTimeLabel() : '';
        const areaLabel = !isEmpty(areaName)
          ? areaName + (sittingTimeLabel ? ` - ${sittingTimeLabel}` : '')
          : sittingTimeLabel;

        return {
          ...table,
          areaLabel,
        };
      });

      const availabilityWithThisAreaOnly = {
        ...availabilitySuggestion,
        tables: tablesWithAreaIncluded,
      };

      return availabilityWithThisAreaOnly;
    })
    .filter((availability) => !isEmpty(availability.tables));

  // Narrow down availabilities by selected experiences’s selectableTime
  if (!isEmpty(booking?.selectedExperiences)) {
    const selectableTimes = booking?.selectedExperiences
      ?.flatMap((e) => e.experience?.selectableTimes)
      ?.map((t) => ({
        startTime: formatIntFromTime(timeStringToTime(t?.startTime, 'h:mma')),
        endTime: formatIntFromTime(timeStringToTime(t?.endTime, 'h:mma')),
        timeOption: t?.timeOption,
        times: t?.times?.map((time) => formatIntFromTime(timeStringToTime(time, 'h:mma'))),
      }));

    availabilities = availabilities?.filter((sittingTime) => {
      const isAvailable = selectableTimes?.some((t) => {
        if (t?.timeOption === 'sitting_times') {
          return t?.times?.some((time) => time === sittingTime.time);
        }
        return sittingTime.time >= t?.startTime && sittingTime.time <= t?.endTime;
      });

      return isAvailable;
    });
  }

  // Merge all availabilities into each unique time
  const availabilitiesDeduped = availabilities.reduce((accum, availability) => {
    const existingObject = findObjectByProperty(accum, availability.time, 'time');

    if (isEmpty(existingObject)) {
      return [...accum, availability];
    }

    const availabilityMerged = {
      ...existingObject,
      tables: [...existingObject.tables, ...availability.tables],
      // session: null, // The merge could have availabilities from multiple sessions
    };
    return updateObjectByProperty(accum, availabilityMerged, 'time');
  }, []);

  // // Dedupe the tables though
  // const withTablesDeduped = availabilitiesDeduped
  //   .map((availability) => ({
  //     ...availability,
  //     tables: unique(availability.tables, 'id'),
  //   }))
  //   .filter((availability) => !isEmpty(availability.tables));

  return availabilitiesDeduped.sort((a, b) => sortByProperty(a, b, 'time'));
};

/**
 * Gets the nearest available suggestion before and after the preferredTime
 * @param {{}[]} availableSuggestions
 * @param {number} preferredTime
 * @returns
 */
export const getNearestAvailableSuggestions = (availableSuggestions, preferredTime) => {
  const nearestBeforePreferredTime = getNearestInArrayOfObjects(
    availableSuggestions,
    'time',
    preferredTime,
    '<',
  );

  const nearestAfterPreferredTime = getNearestInArrayOfObjects(
    availableSuggestions,
    'time',
    preferredTime,
    '>',
  );

  // make sure we don't we return two of the same times
  return unique([nearestBeforePreferredTime, nearestAfterPreferredTime], 'time');
};
